import { useState, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import FormInvitationCreate, { FORM_INVITATION_CREATE_ID } from '../forms/FormInvitationCreate';
import { useTranslation } from 'react-i18next';

/**
 * Sheet pour créer des invitations en batch
 */
export default function SheetInvitationCreate({ open, onClose, procedureUniqId, onSuccess }) {
    const { t } = useTranslation();
    const [canSubmit, setCanSubmit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const formRef = useRef(null);

    const handleClose = useCallback(() => {
        if (!isSubmitting) {
            onClose();
        }
    }, [isSubmitting, onClose]);

    const handleSubmit = async ({
        emails,
        folder_mode,
        folder_id,
        folder_name,
        parent_subfolder_id,
        // [DISABLED] Expiration — décommentez pour réactiver
        // expires_in_days,
        is_otp_required,
    }) => {
        if (!emails.length || isSubmitting) return;

        setIsSubmitting(true);
        try {
            let targetFolderId = folder_id;

            // Si nouveau dossier, le créer d'abord
            if (folder_mode === 'new' && folder_name) {
                const folderResponse = await horodatyApi.createFolder({
                    nom: folder_name,
                });
                targetFolderId = folderResponse.data?.data?.uniq_id;

                if (!targetFolderId) {
                    throw new Error('Échec de la création du dossier');
                }
            }

            // Créer les invitations en parallèle
            const results = await Promise.allSettled(
                emails.map((email) =>
                    horodatyApi.createProcedureInvitation(procedureUniqId, {
                        email,
                        folder_id: targetFolderId,
                        parent_subfolder_id: folder_mode === 'existing' ? parent_subfolder_id : undefined,
                        // [DISABLED] Expiration — décommentez pour réactiver
                        // expires_in_days,
                        is_otp_required,
                    }),
                ),
            );

            const successCount = results.filter((r) => r.status === 'fulfilled').length;
            const failedCount = results.filter((r) => r.status === 'rejected').length;

            if (failedCount === 0) {
                toast.success(t('procedures:invitationSend.sent', { count: successCount }));
            } else if (successCount > 0) {
                toast.warning(
                    `${t('procedures:invitationSend.partialSent', { count: successCount })}, ${t(
                        'procedures:invitationSend.partialFailed',
                        { count: failedCount },
                    )}`,
                );
            } else {
                // Récupérer le message d'erreur de la première erreur
                const firstError = results.find((r) => r.status === 'rejected');
                const errorMessage =
                    firstError?.reason?.response?.data?.message || t('procedures:erreurLorsDeLEnvoiDesInvitations');
                toast.error(errorMessage);
            }

            if (successCount > 0) {
                onSuccess?.();
                onClose();
            }
        } catch (err) {
            console.error('Erreur envoi invitations:', err);
            toast.error(t('procedures:erreurLorsDeLEnvoiDesInvitations'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <SheetContent
                showCloseButton={false}
                className="w-[90vw] md:w-[500px]! md:max-w-[500px]! flex flex-col p-0 rounded-t-[20px] md:rounded-t-none"
                side="right"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-[20px] border-b border-border shrink-0">
                    <h2 className="text-[18px] text-foreground font-medium">{t('procedures:inviterDesTiers')}</h2>
                    <Button
                        variant="plain"
                        size="icon"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="w-[40px] h-[40px] rounded-[8px] bg-muted border border-border text-foreground hover:bg-border"
                    >
                        <X className="h-5 w-5" strokeWidth={1.5} />
                    </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-[20px]">
                    {/* Description */}
                    <p className="text-[14px] text-muted-foreground mb-[20px]">
                        {t('procedures:invitezDesPersonnesExternesARemplirUnRapport')}
                    </p>

                    <FormInvitationCreate ref={formRef} onSubmit={handleSubmit} onCanSubmitChange={setCanSubmit} />
                </div>

                {/* Footer */}
                <div className="p-[20px] border-t border-border shrink-0">
                    <div className="flex gap-[12px]">
                        <Button
                            type="button"
                            variant="soft"
                            color="neutral"
                            onClick={handleClose}
                            disabled={isSubmitting}
                            className="flex-1 rounded-[12px] py-[14px] h-auto"
                        >
                            {t('procedures:annuler')}
                        </Button>
                        <SubmitButton
                            isSubmitting={isSubmitting}
                            loadingText={t('procedures:envoi')}
                            form={FORM_INVITATION_CREATE_ID}
                            disabled={!canSubmit || isSubmitting}
                            className="flex-1 rounded-[12px] py-[14px] h-auto disabled:opacity-50"
                        >
                            <span className="text-[15px]">{t('procedures:envoyerLesInvitations')}</span>
                        </SubmitButton>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
